import React, { ReactElement, useEffect, useState, useRef } from 'react';
import { FormInputMobile, FormTextInput } from '../../../../../../shared/components/crmFormElement/FormInput';
import { VALIDATE_MESSAGES } from '../../../../../../shared/constants/commonConstants';
import { Button, Col, Form, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationDetailModel } from '../../../../../../store/models/applicationDetailModel';
import { RootState } from '../../../../../../store';
import {
  setHospitalSearchValues,
  setHISCardSelected,
  getPatientVisitDetails,
  getPatientAllVisitsDetails,
  setHospitalPatientSearchDetails,
  setPatientSearchDetail,
  setAutomatedNewAdmission,
} from '../../../../../../store/actions/hospitalAction';
import { beneficiarySearchForHISAction, setAdmissionForm, setBeneficiaryList, setBeneficiarySearchResponsePayerId,
  setBenefSearchReqObject,
  setIsAbhaVerified, setIsShowAdmissionForm, setSelectedBeneficiary, setUploadedFiles } from '../../../../../../store/actions/newAdmissionAction';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ADMISSION_FORM_VALUE } from '../../../../../../shared/constants/crmConstants';
import useSetDefaultPayer from '../../../../../../shared/hooks/useSetDefaultPayer';
import { ReactComponent as SearchIcon } from '../../../../../../../src/assets/images/svg/Search_Icon.svg';
import { setPolicySearchResults, setSelectedPolicyDetail } from '../../../../../../store/actions/nhcxAction';
import { NewAdmissionState } from '../../../../../../store/models/newAdmissionModel';
import { centeredNotification } from '../../../../../../utils/utility';
import { customGTMEvent, findSelectedPayer } from '../../../service/crmUtils';
import { setDeeplinkDto } from '../../../../../../store/actions/authAction';
import { DeeplinkDto } from '../../../../../../store/models/authModel';
import { Payer } from '../../../../../../shared/models/payer.model';

const HISSearchRedesign = (props: any): ReactElement | null => {
  const { selectedHISOption, form, onSearchSuccess, onSearchError, resetMultipleSteps, setSelectedOption,
    resetHISForm, onFieldChange } = props;
  const dispatch = useDispatch();
  const [baseForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showFields, isShowFields] = useState<any>({});
  const appDetail = useSelector((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const [disableSearch, setDisableSearch] = useState<boolean>(false);
  const {isVisitsEnabled, hisid, hisProviderID,
    integrationSystemFields: hisData} = appDetail?._applicationDetail?.hospitalDetail?.providerConfigurableProperties;
  const hisFields = JSON.parse(hisData)?.HISFields;

  const loggedInUserId = appDetail?._applicationDetail?.userDetail?.userId;
  const { providerId } = appDetail?._applicationDetail?.hospitalDetail;
  const setDefaultPayer = useSetDefaultPayer();
  const [isUserInputFocused, setIsUserInputFocused] = useState(false);
  const [isFieldInFocus, setIsFieldInFocus] = useState(false);
  const [hasProcessedDeeplink, setHasProcessedDeeplink] = useState(false);
  const { deeplinkDto } = useSelector((state: RootState): any => state.auth);
  const { beneficiarySearchResponsePayerId }: any = useSelector((state: RootState): NewAdmissionState => state.newAdmission);
  const { payer, allPayers } = useSelector((state: RootState): any => state.payerList);
  const { selectedPatientDetail, isAutomatedNewAdmission } = useSelector((state: RootState): any => state.hospital);
  // Coordination state for parallel API calls
  const [hisSearchProcessed, setHisSearchProcessed] = useState(false);
  const [pendingHISBenefResponse, setPendingHISBenefResponse] = useState<any>(null);
  const isCoordinatedCallRef = useRef(false);  // Coordinated callback functions for parallel API calls

  const beneficiarySearchSuccess = (benefList: any[], memberSearchRequest: Record<string, any>): void => {
    setLoading(false);
    if (!payer?.isBenefSearchEnabled) {
      return;
    }
    if (beneficiarySearchResponsePayerId > 0) {
      const finalPayer = allPayers.find((payerItem: Payer): boolean =>
        (payerItem.id === beneficiarySearchResponsePayerId && payerItem.isGovtScheme === false));
      if (!finalPayer) {
        dispatch(setBeneficiarySearchResponsePayerId(0));
        return;
      }
    }
    if (!isEmpty(memberSearchRequest)) {
      dispatch(setBeneficiaryList(benefList));
      dispatch(setBenefSearchReqObject(memberSearchRequest));
    }
  };

  const failedBeneficiarySearch = (errorMsg: string, allowNextStage: boolean, retryMessage: string): void => {
    setLoading(false);
    dispatch(setAutomatedNewAdmission(false)); // stop automation
    customGTMEvent({
      event: `Beneficiary search failure for ${payer.shortName}`,
      payer: payer.name,
    });
    if (retryMessage !== 'Bad Gateway') {
      centeredNotification(errorMsg, 'info');
    } else {
      centeredNotification(errorMsg);
    }
  };

  const handleBenefResponse = (isSuccess: boolean, ...args: any[]): void => {
    if (!isCoordinatedCallRef.current) {
      return;
    }
    if (hisSearchProcessed && !isEmpty(selectedPatientDetail)) {
      // Execute immediately
      if (isSuccess) {
        beneficiarySearchSuccess(args[0], args[1]);
      } else {
        failedBeneficiarySearch(args[0], args[1], args[2]);
      }
    } else {
      // Queue for later
      const response = isSuccess
        ? { benefList: args[0], HISMemberSearchRequest: args[1], type: 'success' }
        : { errorMsg: args[0], allowNextStage: args[1], retryMessage: args[2], type: 'error' };
      setPendingHISBenefResponse(response);
    }
  };

  const resetBenefCoordinationState = (): void => {
    // Reset coordination state
    setPendingHISBenefResponse(null);
    setHisSearchProcessed(false);
    isCoordinatedCallRef.current = false;
  };

  // Process pending second API response when first API completes
  useEffect((): void => {
    if (isCoordinatedCallRef.current && hisSearchProcessed && pendingHISBenefResponse &&
      !isEmpty(selectedPatientDetail)
    ) {
      if (!isAutomatedNewAdmission) {
        resetBenefCoordinationState();
        return;
      }
      // Delay execution to ensure the HIS response is processed
      setTimeout((): void => {
        const HISPayerId = selectedPatientDetail?.patientDetails?.payerID || '';
        const selectedPayer = findSelectedPayer(allPayers, HISPayerId);
        if (selectedPayer?.id !== 0) {
          if (pendingHISBenefResponse.type === 'success') {
            beneficiarySearchSuccess(
              pendingHISBenefResponse.benefList,
              pendingHISBenefResponse.HISMemberSearchRequest,
            );
          } else if (pendingHISBenefResponse.type === 'error') {
            failedBeneficiarySearch(
              pendingHISBenefResponse.errorMsg,
              pendingHISBenefResponse.allowNextStage,
              pendingHISBenefResponse.retryMessage,
            );
          }
        } else {
          failedBeneficiarySearch(
            pendingHISBenefResponse.errorMsg,
            pendingHISBenefResponse.allowNextStage,
            pendingHISBenefResponse.retryMessage,
          );
        }
        resetBenefCoordinationState();
      }, 500);
    }
  }, [hisSearchProcessed, pendingHISBenefResponse, payer]);

  useEffect((): any => {
    fields();
  }, []);

  // Handle deeplink automation when deeplinkDto, selectedHISOption, or showFields change
  useEffect((): void => {
    if (!isEmpty(deeplinkDto) && !isEmpty(showFields) && !hasProcessedDeeplink) {
      dispatch(setAutomatedNewAdmission(true)); // Set flag to indicate automated admission and prevent form resets
      const { ipNumber } = deeplinkDto.deeplinkQueryParams || {};
      if (ipNumber && selectedHISOption && showFields[selectedHISOption]?.key) {
        const fieldKey = showFields[selectedHISOption]?.key;
        const formVal = { [fieldKey]: ipNumber };
        baseForm.setFieldsValue(formVal);
        setIsUserInputFocused(true);
        // Mark as processed to prevent multiple executions
        setHasProcessedDeeplink(true);
        dispatch(setDeeplinkDto({} as DeeplinkDto)); // Clear deeplink data after processing
        const HISBenefSearchPayload =  {
          ...formVal,
          'loggedInUserId': loggedInUserId,
          'hisMasterID': hisid,
          'hisProviderID': hisProviderID,
          'providerId': providerId,
        };
        // Enable coordination mode and reset state
        isCoordinatedCallRef.current = true;
        setHisSearchProcessed(false);
        setPendingHISBenefResponse(null);

        // Trigger both APIs simultaneously after a short delay to ensure form is ready
        setTimeout((): void => {
          onClickSearch(formVal);
          dispatch(beneficiarySearchForHISAction(
            HISBenefSearchPayload,
            (benefList: any[], memberSearchRequest: Record<string, any>): void => {
              handleBenefResponse(true, benefList, memberSearchRequest);
            },
            (errorMsg: string, allowNextStage: boolean, retryMessage: string): void => {
              handleBenefResponse(false, errorMsg, allowNextStage, retryMessage);
            },
          ));
        }, 100);
      }
    }
  }, [selectedHISOption, hasProcessedDeeplink]);

  // Extracting "key," "type," and "placeholder" for each field
  const fields = (): any => {
    const extractedFields: any = {};
    for (const fieldName in hisFields) {
      if (hisFields.hasOwnProperty(fieldName)) {
        const fieldData = hisFields[fieldName];
        extractedFields[fieldName] = {
          key: fieldData?.key,
          type: fieldData?.type,
          placeholder: fieldData?.placeholder,
          label: fieldData?.label,
        };
      }
    }
    const dataForState = { ...extractedFields };
    isShowFields(dataForState);
  };

  const onSuccess = (): void => {
    if (isCoordinatedCallRef.current) {
      setHisSearchProcessed(true);
    }
    setLoading(false);
    onSearchSuccess();
  };

  const onError = (): void => {
    if (isCoordinatedCallRef.current) {
      setHisSearchProcessed(true);
    }
    setLoading(false);
    dispatch(setAutomatedNewAdmission(false));
    onSearchError();
  };

  const validateFields = (): any => {
    setTimeout((): any => {
      baseForm
        ?.validateFields()
        .then((): any => {
          setDisableSearch(true);
        })
        .catch((error: any): any => {
          const { errorFields } = error;
          if (errorFields?.length > 0) {
            setDisableSearch(false);
          }
        });
    }, 200);
  };

  const onClickSearch = (formVal: any): any => {
    validateFields();
    setSelectedOption('');
    resetMultipleSteps(['searchActivation', 'patientCard', 'memberOrPolicy',
      'policyCard', 'memberSearch', 'claimReviewed',
      'documentsAttached', 'submitClaim', 'memberCard',
    ]);
    setSelectedBeneficiary('');
    dispatch(setHospitalPatientSearchDetails([]));
    dispatch(setHISCardSelected(false));
    dispatch(setPatientSearchDetail([]));
    dispatch(setPolicySearchResults([]));
    dispatch(setSelectedPolicyDetail(null));
    const HISVisitsPayload = {
      ...(formVal?.maxID ? { uhid: formVal.maxID } : formVal),
      'loggedInUserId': loggedInUserId,
      'hisMasterID': hisid,
      'hisProviderID': hisProviderID,
    };
    setLoading(true);
    if (isVisitsEnabled) {
      dispatch(getPatientAllVisitsDetails(HISVisitsPayload, onSuccess, onError));
    } else {
      const HISPatientPayload = {
        ...HISVisitsPayload,
        'visitCode': '0',
        'visitType': '',
      };
      dispatch(getPatientVisitDetails(HISPatientPayload, onSuccess, onError));
    }
    dispatch(setHospitalSearchValues(formVal));
    form?.resetFields(['payer']);
    setDefaultPayer();
  };

  useEffect((): void => {
    const ipNumber = deeplinkDto?.deeplinkQueryParams?.ipNumber;
    if (!ipNumber) {
      // Only reset form if we don't have a URL parameter to preserve
      baseForm.resetFields();
      form?.resetFields(['payer']);
      dispatch(setHospitalPatientSearchDetails([]));
      dispatch(setHISCardSelected(false));
      setDefaultPayer();
      initiateMemberForm();
      // Reset the deeplink processing state
      setHasProcessedDeeplink(false);
    }
  }, [selectedHISOption, resetHISForm]);

  const initiateMemberForm = (): void => {
    dispatch(setIsShowAdmissionForm(false));
    dispatch(setSelectedBeneficiary(null));
    dispatch(setAdmissionForm(cloneDeep(DEFAULT_ADMISSION_FORM_VALUE)));
    dispatch(setUploadedFiles([]));
    dispatch(setIsAbhaVerified(false, {}));
  };

  const handleUserUnFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    setIsFieldInFocus(false);
    const inputValue = e.target.value;
    if (inputValue) {
      setIsUserInputFocused(true); // Keep focused if there's a value
    } else {
      setTimeout((): void => setIsUserInputFocused(false), 100); // Otherwise, remove focus with delay
    }
  };

  const handleUserFocus = (): void => {
    setIsUserInputFocused(true);
    setIsFieldInFocus(true);
  };

  const HISSearchUi = (): ReactElement => {
    const field = showFields?.[selectedHISOption];

    const commonProps = {
      key: field?.key,
      placeholder: isUserInputFocused ? '' : field?.placeholder,
      size: 22,
      label: field?.label,
      onFocus: handleUserFocus,
      onBlur: handleUserUnFocus,
      floatLabel: isUserInputFocused,
      hideLabel: true,
      fieldInFocus: isFieldInFocus,
      className: 'HISSearchUI',
      onChange: onFieldChange,
      disabled: loading,
      required: true,
    };

    if (field?.type === 'Mobile') {
      return (
        <FormInputMobile
          detail={{
            ...commonProps,
          }}
        />
      );
    }

    return (
      <FormTextInput
        detail={{
          ...commonProps,
        }}
      />
    );
  };

  return (
    <>
      <Form
        layout="horizontal"
        form={baseForm}
        validateMessages={VALIDATE_MESSAGES}
        onFinish={onClickSearch}
        requiredMark="optional"
        noValidate={true}
        >
        <Row className="HIS-search-div search-align-items">
          <Col md={22} className="align-his-search-field">
            {HISSearchUi()}
          </Col>
          <Col md={2} className="HIS-search-btn">
            <Button
              data-testid="beneficiary-search-button"
              type={!disableSearch ? undefined : 'primary'}
              htmlType="submit"
              loading={loading}
              className={`NA-his-next-button HIS-top-10 benef-submit-btn`}
              icon={<SearchIcon className="his-search-icon"/>}
              />
          </Col>
        </Row>
      </Form>
    </>
  );
};
export default HISSearchRedesign;
